import { collectNestedSprintDataWithLoad, serializeForClipboard } from '../clipboardUtils';
import { showToast } from '../../../../Toast';
import { showBusyOverlay } from '../../../../BusyOverlay';
import { isLargeOperation } from './clipboardSize';

/**
 * Load nested campaigns, serialize, remember locally, then write the system clipboard.
 * Returns false when a nested campaign failed to load. A clipboard-write failure still
 * returns true — the local copy is enough for duplicate and paste fallback.
 */
export async function copyTasksToClipboard({
  tasksToCopy,
  nodePositions,
  nestedSprints,
  allSprints,
  loadFullSprint,
  allTags,
  customFields,
  busyMessage,
  successLog,
  remember,
}) {
  const hideBusy = isLargeOperation(tasksToCopy) ? showBusyOverlay(busyMessage) : null;
  try {
    const { nestedSprintData, missingNestedSprintIds, danglingNestedSprintIds } = await collectNestedSprintDataWithLoad(
      tasksToCopy,
      nestedSprints,
      allSprints,
      loadFullSprint,
    );

    // Abort only on transient load failures (campaigns that exist but didn't load).
    // Copying anyway would drop real subtrees from the clipboard (unrecoverable).
    if (missingNestedSprintIds && missingNestedSprintIds.length > 0) {
      console.warn('[COPY] Aborted - nested campaigns not fully loaded:', missingNestedSprintIds);
      showToast(
        "Couldn't copy: some nested campaigns are still loading. Please try again in a moment.",
        { type: 'error' },
      );
      return false;
    }

    // Dangling references (parent campaign deleted) are dead links — they don't
    // block the copy and are harmlessly dropped on paste.
    if (danglingNestedSprintIds && danglingNestedSprintIds.length > 0) {
      console.warn('[COPY] Ignoring dangling nested campaign reference(s):', danglingNestedSprintIds);
    }

    const clipboardData = serializeForClipboard(
      tasksToCopy,
      nodePositions,
      nestedSprintData,
      allTags,
      customFields,
    );
    remember?.(clipboardData);

    try {
      await navigator.clipboard.writeText(JSON.stringify(clipboardData, null, 2));
      console.log(successLog);
    } catch (error) {
      console.error('Failed to write to clipboard:', error);
    }
    return true;
  } finally {
    hideBusy?.();
  }
}
